import { useState, useEffect, useRef } from 'react'
import Button from './Button'

// Google Fonts API key - UPDATE THIS KEY
const GOOGLE_FONTS_API_KEY = 'AIzaSyDZKSU6e1rl5ccUVh0aLv_a8XEMSmFROvY'

// Bunny Fonts API (privacy-friendly Google Fonts alternative)
const BUNNY_FONTS_API = 'https://fonts.bunny.net/list'

// Font categories
const FONT_CATEGORIES = {
  all: { label: 'All Fonts', icon: '🔤' },
  scripts: { label: 'Scripts & Handwriting', icon: '✍️' },
  sansSerif: { label: 'Sans Serif', icon: '🅰️' },
  serif: { label: 'Serif', icon: '📰' },
  display: { label: 'Display & Decorative', icon: '🎨' },
  monospace: { label: 'Monospace', icon: '💻' },
  cricutFriendly: { label: 'Cricut-Friendly', icon: '✂️' },
  embroiderySafe: { label: 'Embroidery-Safe', icon: '🧵' },
}

// Cricut-friendly fonts (thick, clean, cut well)
const CRICUT_FRIENDLY = new Set([
  'Roboto', 'Montserrat', 'Bebas Neue', 'Anton', 'Oswald', 'Poppins',
  'Raleway', 'Quicksand', 'Comfortaa', 'Nunito', 'Righteous', 'Fredoka One',
  'Titan One', 'Passion One', 'Black Ops One', 'Alfa Slab One', 'Bungee',
  'Rubik', 'Work Sans', 'DM Sans', 'Cabin', 'Karla', 'Barlow', 'Inter',
  'Outfit', 'Lexend', 'Teko', 'Archivo Black', 'Fjalla One', 'Russo One',
])

// Embroidery-safe fonts (bold, no thin strokes)
const EMBROIDERY_SAFE = new Set([
  'Anton', 'Bebas Neue', 'Oswald', 'Teko', 'Archivo Black', 'Roboto',
  'Montserrat', 'Poppins', 'Work Sans', 'Inter', 'Rubik', 'Nunito',
  'Quicksand', 'Comfortaa', 'Cabin', 'Righteous', 'Fredoka One', 'Titan One',
  'Passion One', 'Alfa Slab One', 'Black Ops One', 'Bungee', 'Ultra',
])

// Map Google/Bunny categories to ours
const mapCategory = (cat) => {
  const map = {
    'sans-serif': 'sansSerif',
    'serif': 'serif',
    'display': 'display',
    'handwriting': 'scripts',
    'monospace': 'monospace',
  }
  return map[cat] || 'display'
}

// Fallback fonts if APIs fail
const FALLBACK_FONTS = [
  { family: 'Roboto', category: 'sansSerif', source: 'fallback' },
  { family: 'Open Sans', category: 'sansSerif', source: 'fallback' },
  { family: 'Montserrat', category: 'sansSerif', source: 'fallback' },
  { family: 'Lato', category: 'sansSerif', source: 'fallback' },
  { family: 'Poppins', category: 'sansSerif', source: 'fallback' },
  { family: 'Playfair Display', category: 'serif', source: 'fallback' },
  { family: 'Merriweather', category: 'serif', source: 'fallback' },
  { family: 'Dancing Script', category: 'scripts', source: 'fallback' },
  { family: 'Pacifico', category: 'scripts', source: 'fallback' },
  { family: 'Great Vibes', category: 'scripts', source: 'fallback' },
  { family: 'Bebas Neue', category: 'display', source: 'fallback' },
  { family: 'Anton', category: 'display', source: 'fallback' },
  { family: 'Lobster', category: 'display', source: 'fallback' },
  { family: 'Oswald', category: 'sansSerif', source: 'fallback' },
  { family: 'Raleway', category: 'sansSerif', source: 'fallback' },
]

// Text layout options
const TEXT_LAYOUTS = [
  { id: 'straight', label: 'Straight', icon: '—' },
  { id: 'curved-up', label: 'Curved Up', icon: '⌒' },
  { id: 'curved-down', label: 'Curved Down', icon: '⌓' },
  { id: 'circle', label: 'Circle', icon: '○' },
  { id: 'wavy', label: 'Wavy', icon: '∿' },
]

// ============================================================================
// TEXT TOOL COMPONENT
// ============================================================================

const TextTool = ({ onTextGenerated, onClose }) => {
  const [text, setText] = useState('')
  const [selectedFont, setSelectedFont] = useState('Montserrat')
  const [fontSize, setFontSize] = useState(72)
  const [letterSpacing, setLetterSpacing] = useState(0)
  const [textColor, setTextColor] = useState('#000000')
  const [layout, setLayout] = useState('straight')
  const [category, setCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  // Font loading state
  const [allFonts, setAllFonts] = useState([])
  const [isLoadingFonts, setIsLoadingFonts] = useState(true)
  const [loadingStatus, setLoadingStatus] = useState('Initializing...')
  const [loadedFontFamilies, setLoadedFontFamilies] = useState(new Set(['Montserrat']))

  const canvasRef = useRef(null)
  const fontListRef = useRef(null)
  const previewRef = useRef(null)

  // ============================================================================
  // LOAD ALL FONTS FROM MULTIPLE APIS
  // ============================================================================
  useEffect(() => {
    const loadAllFonts = async () => {
      setIsLoadingFonts(true)
      const fontsMap = new Map()

      // 1. GOOGLE FONTS (~1,500 fonts)
      setLoadingStatus('Loading Google Fonts...')
      try {
        const res = await fetch(
          `https://www.googleapis.com/webfonts/v1/webfonts?key=${GOOGLE_FONTS_API_KEY}&sort=popularity`
        )
        const data = await res.json()
        if (data.items) {
          data.items.forEach((font, idx) => {
            fontsMap.set(font.family.toLowerCase(), {
              family: font.family,
              category: mapCategory(font.category),
              source: 'google',
              popularity: idx + 1,
            })
          })
          console.log(`✓ Loaded ${data.items.length} Google Fonts`)
        }
      } catch (e) {
        console.error('Google Fonts error:', e)
      }

      // 2. BUNNY FONTS (privacy-friendly alternative, ~1,500 fonts)
      setLoadingStatus('Loading Bunny Fonts...')
      try {
        const res = await fetch(BUNNY_FONTS_API)
        const data = await res.json()
        let added = 0
        Object.entries(data).forEach(([family, info]) => {
          const key = family.toLowerCase()
          if (!fontsMap.has(key)) {
            fontsMap.set(key, {
              family: family,
              category: mapCategory(info.category),
              source: 'bunny',
            })
            added++
          }
        })
        console.log(`✓ Added ${added} unique Bunny Fonts`)
      } catch (e) {
        console.error('Bunny Fonts error:', e)
      }

      // 3. FONT SQUIRREL (skipped - CORS blocked in browser)
      setLoadingStatus('Loading Font Squirrel...')
      try {
        console.log('⚠️ Skipping Font Squirrel (CORS blocked in browser)')
      } catch (e) {
        console.error('Font Squirrel error:', e)
      }

      // Convert to array and sort
      let fontsArray = Array.from(fontsMap.values())

      // Fallback if all APIs failed
      if (fontsArray.length === 0) {
        fontsArray = FALLBACK_FONTS
      }

      // Sort by popularity (Google) then alphabetically
      fontsArray.sort((a, b) => {
        if (a.source === 'google' && b.source !== 'google') return -1
        if (a.source !== 'google' && b.source === 'google') return 1
        if (a.popularity && b.popularity) return a.popularity - b.popularity
        return a.family.localeCompare(b.family)
      })

      setAllFonts(fontsArray)
      setLoadingStatus(`Loaded ${fontsArray.length.toLocaleString()} fonts!`)
      setIsLoadingFonts(false)

      // Pre-load the default font
      loadFont('Montserrat')
    }

    loadAllFonts()
  }, [])

  // Load a font dynamically
  const loadFont = (family) => {
    if (loadedFontFamilies.has(family)) return

    const link = document.createElement('link')
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;700&display=swap`
    link.rel = 'stylesheet'
    document.head.appendChild(link)

    setLoadedFontFamilies((prev) => new Set([...prev, family]))
  }

  // Load font when selected
  useEffect(() => {
    loadFont(selectedFont)
  }, [selectedFont])

  // Filter fonts based on category and search
  const filteredFonts = (() => {
    let fonts = allFonts

    // Filter by category
    if (category === 'cricutFriendly') {
      fonts = fonts.filter((f) => CRICUT_FRIENDLY.has(f.family))
    } else if (category === 'embroiderySafe') {
      fonts = fonts.filter((f) => EMBROIDERY_SAFE.has(f.family))
    } else if (category !== 'all') {
      fonts = fonts.filter((f) => f.category === category)
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      fonts = fonts.filter((f) => f.family.toLowerCase().includes(query))
    }

    return fonts
  })()

  // Lazy load fonts as user scrolls
  const handleScroll = () => {
    if (!fontListRef.current) return

    const { scrollTop, clientHeight } = fontListRef.current
    const itemHeight = 36
    const startIdx = Math.floor(scrollTop / itemHeight)
    const endIdx = startIdx + Math.ceil(clientHeight / itemHeight) + 3

    filteredFonts.slice(startIdx, endIdx).forEach((font) => {
      loadFont(font.family)
    })
  }

  // Generate text image
  const generateTextImage = async () => {
    if (!text.trim()) return

    setIsGenerating(true)

    try {
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      const padding = 40

      // Measure text
      ctx.font = `${fontSize}px "${selectedFont}"`
      const textWidth = ctx.measureText(text).width
      const textHeight = fontSize * 1.2

      // Set canvas size
      canvas.width = textWidth + padding * 2
      canvas.height = textHeight + padding * 2

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Set text style
      ctx.font = `${fontSize}px "${selectedFont}"`
      ctx.fillStyle = textColor
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      // Draw based on layout
      if (layout === 'straight') {
        if (letterSpacing !== 0) {
          const chars = text.split('')
          let x = centerX - (textWidth + (chars.length - 1) * letterSpacing) / 2
          chars.forEach((char) => {
            const charWidth = ctx.measureText(char).width
            ctx.fillText(char, x + charWidth / 2, centerY)
            x += charWidth + letterSpacing
          })
        } else {
          ctx.fillText(text, centerX, centerY)
        }
      } else if (layout === 'curved-up' || layout === 'curved-down') {
        const isUp = layout === 'curved-up'
        const chars = text.split('')
        const arcAngle = Math.PI * 0.6
        const radius = textWidth * 0.6
        const startAngle = isUp ? Math.PI + (Math.PI - arcAngle) / 2 : (Math.PI - arcAngle) / 2
        const angleStep = arcAngle / (chars.length - 1 || 1)

        chars.forEach((char, i) => {
          const angle = startAngle + i * angleStep * (isUp ? 1 : -1)
          const x = centerX + radius * Math.cos(angle)
          const y = centerY + radius * Math.sin(angle) * (isUp ? -1 : 1)
          ctx.save()
          ctx.translate(x, y)
          ctx.rotate(angle + (isUp ? Math.PI / 2 : -Math.PI / 2))
          ctx.fillText(char, 0, 0)
          ctx.restore()
        })
      } else if (layout === 'circle') {
        const chars = text.split('')
        const radius = Math.min(textWidth, textHeight) * 0.4
        const angleStep = (2 * Math.PI) / chars.length

        chars.forEach((char, i) => {
          const angle = -Math.PI / 2 + i * angleStep
          const x = centerX + radius * Math.cos(angle)
          const y = centerY + radius * Math.sin(angle)
          ctx.save()
          ctx.translate(x, y)
          ctx.rotate(angle + Math.PI / 2)
          ctx.fillText(char, 0, 0)
          ctx.restore()
        })
      } else if (layout === 'wavy') {
        const chars = text.split('')
        const charWidth = ctx.measureText('M').width
        const startX = centerX - (chars.length * charWidth) / 2
        const amplitude = fontSize * 0.3

        chars.forEach((char, i) => {
          const x = startX + i * charWidth
          const y = centerY + Math.sin(i * 0.5) * amplitude
          ctx.fillText(char, x, y)
        })
      }

      // Convert to image
      const imageUrl = canvas.toDataURL('image/png')

      if (onTextGenerated) {
        onTextGenerated({
          url: imageUrl,
          text,
          font: selectedFont,
          fontSize,
          color: textColor,
          layout,
        })
      }
    } catch (error) {
      console.error('Text generation error:', error)
      alert('Failed to generate text image')
    } finally {
      setIsGenerating(false)
    }
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-zenna flex items-center justify-center">
            <span className="text-white text-xl">T</span>
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-slate-800 dark:text-white">
              Text Tool
            </h2>
            <p className="text-xs text-slate-500">
              {isLoadingFonts
                ? loadingStatus
                : `${allFonts.length.toLocaleString()} fonts • Perfect spelling, every time ✨`}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Controls */}
        <div className="w-80 border-r border-slate-200 dark:border-slate-700 p-4 overflow-y-auto space-y-4">
          {/* Text Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Your Text
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your text here..."
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-zenna-violet/50 resize-none"
              rows={3}
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Font Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(FONT_CATEGORIES).map(([key, { label, icon }]) => (
                <button
                  key={key}
                  onClick={() => setCategory(key)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    category === key
                      ? 'bg-zenna-violet text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <span>{icon}</span>
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Search Fonts
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search fonts..."
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-zenna-violet/50"
            />
          </div>

          {/* Font List */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {isLoadingFonts ? 'Loading fonts...' : `Select Font (${filteredFonts.length.toLocaleString()})`}
            </label>
            <div
              ref={fontListRef}
              onScroll={handleScroll}
              className="h-48 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-xl"
            >
              {isLoadingFonts ? (
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <div className="animate-spin w-6 h-6 border-2 border-zenna-violet border-t-transparent rounded-full" />
                  <span className="text-xs text-slate-400">{loadingStatus}</span>
                </div>
              ) : filteredFonts.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                  No fonts found
                </div>
              ) : (
                filteredFonts.map((font) => (
                  <button
                    key={font.family}
                    onClick={() => setSelectedFont(font.family)}
                    onMouseEnter={() => loadFont(font.family)}
                    className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between ${
                      selectedFont === font.family
                        ? 'bg-zenna-violet/10 text-zenna-violet dark:text-zenna-cyan border-l-2 border-zenna-violet'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                    style={{ fontFamily: `"${font.family}", sans-serif` }}
                  >
                    <span>{font.family}</span>
                    {font.source && font.source !== 'google' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                        {font.source}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Size and Spacing */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Size: {fontSize}px
              </label>
              <input
                type="range"
                min="24"
                max="200"
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full accent-zenna-violet"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Spacing: {letterSpacing}px
              </label>
              <input
                type="range"
                min="-10"
                max="30"
                value={letterSpacing}
                onChange={(e) => setLetterSpacing(Number(e.target.value))}
                className="w-full accent-zenna-violet"
              />
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Text Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="w-10 h-10 rounded-lg cursor-pointer border-0"
              />
              <input
                type="text"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white font-mono text-sm"
              />
              <div className="flex gap-1">
                {['#000000', '#ffffff', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#ec4899'].map((color) => (
                  <button
                    key={color}
                    onClick={() => setTextColor(color)}
                    className="w-6 h-6 rounded-full border-2 border-slate-300 dark:border-slate-500 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Layout */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Layout
            </label>
            <div className="flex gap-2">
              {TEXT_LAYOUTS.map(({ id, label, icon }) => (
                <button
                  key={id}
                  onClick={() => setLayout(id)}
                  title={label}
                  className={`flex-1 py-2 rounded-lg text-lg transition-colors ${
                    layout === id
                      ? 'bg-zenna-violet text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 p-4 flex flex-col">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Preview
          </label>
          <div
            ref={previewRef}
            className="flex-1 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: '#ffffff' }}
          >
            {text ? (
              <div
                style={{
                  fontFamily: `"${selectedFont}", sans-serif`,
                  fontSize: `${Math.min(fontSize, 120)}px`,
                  color: textColor,
                  letterSpacing: `${letterSpacing}px`,
                  textAlign: 'center',
                  padding: '20px',
                  wordBreak: 'break-word',
                  textShadow: textColor === '#ffffff' || textColor === '#FFFFFF' 
                    ? '0 0 2px rgba(0,0,0,0.4), 0 0 4px rgba(0,0,0,0.2)' 
                    : 'none',
                }}
              >
                {text}
              </div>
            ) : (
              <p className="text-slate-400 dark:text-slate-500">Type something to preview</p>
            )}
          </div>

          {/* Hidden canvas for rendering */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Generate Button */}
          <div className="mt-4 flex gap-3">
            <Button onClick={generateTextImage} disabled={!text.trim() || isGenerating} className="flex-1">
              {isGenerating ? (
                <>
                  <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add to Canvas
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TextTool